<template>
  <div v-if="checkoutSteps.length > 0" class="w-full">
    <!-- The segmented progress bar -->
    <div class="flex w-full items-center gap-2">
      <div
        v-for="(_, index) in checkoutSteps"
        :key="`segment-${index}`"
        class="h-[1px] flex-1 first:rounded-s-none last:rounded-e-none rounded-full checkout-step-segment"
        :class="[
          index <= currentStepIndex ? barHighlightedColor : barDefaultColor,
          `checkout-step-${index + 1}`,
          index < currentStepIndex
            ? 'checkout-step-completed'
            : index === currentStepIndex
            ? 'checkout-step-current'
            : 'checkout-step-upcoming',
        ]"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCssVar } from '@vueuse/core/index'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useRoute } from 'vue-router'

import { ROOM_MAP_BG_MODE, RoomMapBgMode } from '@/common/interfaces/RoomMap'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'

import { FeatureFlag } from '@generated/types'

interface Step {
  name: string
  routes: string[]
}

const checkoutStore = useCheckoutStore()
const settingStore = useSettingStore()
const route = useRoute()

const { standingRoomOnly, performance, hasAddOns } = storeToRefs(checkoutStore)

const checkoutSteps = computed<Step[]>(() => {
  const steps: Step[] = []

  steps.push({ name: 'Guests', routes: ['setGuests'] })

  if (!standingRoomOnly.value) {
    steps.push({ name: 'Tables', routes: ['setTable'] })
  }

  const skipSeatingTime =
    settingStore.flagEnabled(FeatureFlag.SKIP_SEATING_TIME) &&
    performance.value?.seating_times?.length === 1
  if (
    !skipSeatingTime &&
    performance.value?.seating_times &&
    performance.value.seating_times.length > 0
  ) {
    steps.push({ name: 'Seating Time', routes: ['setSeatingTime'] })
  }

  if (hasAddOns.value) {
    steps.push({ name: 'Add-ons', routes: ['setAddOns'] })
  }

  steps.push({ name: 'Payment', routes: ['payment'] })

  steps.push({ name: 'Order Confirmation', routes: ['order-confirmation', 'order-details'] })

  return steps
})

const currentStepIndex = computed<number>(() => {
  const currentRouteName = route.name as string
  if (!currentRouteName) return -1

  return checkoutSteps.value.findIndex(step => step.routes.includes(currentRouteName))
})

// Bar styling
const mode = useCssVar('--mode')
const roomMapBgMode = computed<RoomMapBgMode>(() => {
  return mode.value || ROOM_MAP_BG_MODE.FORCE_LIGHT
})
const isLight = computed<boolean>(() => roomMapBgMode.value === ROOM_MAP_BG_MODE.FORCE_LIGHT)
const barHighlightedColor = computed(() => (isLight.value ? 'bg-black' : 'bg-white'))
const barDefaultColor = computed(() => (isLight.value ? 'bg-gray-300' : 'bg-gray-600'))
</script>
